// Shared Help Center guide index — consumed by the docs portal (HelpHub)
// and the ⌘K command palette. `shot` is the screen key; the actual image
// resolves to the backend-served (fresh) asset with a bundled fallback.
import { Upload, KanbanSquare, Mail, Send, Chrome, Eye } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

// Fresh, one-click-refreshable screenshot served by the backend.
export const shotPrimary = (key) => `${BACKEND}/api/help-assets/${key}.png`;
// Bundled fallback that always ships with the frontend build.
export const shotFallback = (key) => `/help/${key}.png`;

export const GROUP_ORDER = ['Getting started', 'Outreach', 'Data & insights'];

export const GUIDES = [
  {
    id: 'import-leads', icon: Upload, shot: 'leads', group: 'Getting started',
    title: 'Import Leads', shotCaption: 'The Leads board — Import lives in the top toolbar.',
    summary: 'Bring your existing contacts into the CRM in minutes.',
    tags: ['csv', 'upload', 'contacts', 'data', 'migrate'],
    cta: { label: 'Go to Leads', href: '/leads' },
    reference: [
      ['Name', 'Required. The contact’s full name.'],
      ['Email / Phone', 'Recommended. Leads without either are skipped by auto-cadence until added.'],
      ['Company', 'Groups leads and powers ICP scoring.'],
      ['Source', 'Keeps your reports clean — always include it.'],
    ],
    steps: [
      { title: 'Open the Import dialog', text: 'Open Leads and click Import (top toolbar). Download the sample CSV template first so your columns line up.' },
      { title: 'Map your columns', text: 'Map your columns — at minimum Name; add Email, Phone, Company and Source for the best results.' },
      { title: 'Review the preview', text: 'Review the preview. Leads without an email or phone are tagged so they skip auto-cadence until you add contact info.' },
      { title: 'Confirm the import', text: 'Confirm import. Large files upload in chunks with a live progress bar — you can keep working.' },
    ],
    best: 'De-dupe before importing (the CRM flags likely duplicates), and always include a Source so your reports stay clean.',
  },
  {
    id: 'build-pipeline', icon: KanbanSquare, shot: 'pipeline', group: 'Getting started',
    title: 'Build Your Pipeline', shotCaption: 'Deals — track opportunities across your stages.',
    summary: 'Set up stages and move deals from first touch to won.',
    tags: ['pipeline', 'stages', 'kanban', 'deals', 'board'],
    cta: { label: 'Open Deals', href: '/deals' },
    reference: [
      ['Stage', 'Where a deal sits in your funnel (New → Won/Lost).'],
      ['Priority', 'Low / Medium / High / Critical — surface the hottest first.'],
      ['AI Score', 'Model-ranked likelihood to convert.'],
      ['Amount', 'Deal value; rolls up into pipeline reports.'],
    ],
    steps: [
      { title: 'Define your stages', text: 'In Lead Settings, define your pipeline stages (e.g. New → Contacted → Qualified → Won/Lost).' },
      { title: 'Work the board', text: 'Drag lead cards across the Kanban board to update their stage — changes save instantly.' },
      { title: 'Filter and export', text: 'Use the filter bar (status, source, owner, date) to focus on a slice, then Export CSV to share it.' },
      { title: 'Prioritise with AI', text: 'Watch the AI Score and Engagement badges to prioritise the hottest leads first.' },
    ],
    best: 'Keep stages to 5–7 max. Too many stages make the board noisy and slow your team down.',
  },
  {
    id: 'connect-email', icon: Mail, shot: 'integrations', group: 'Outreach',
    title: 'Connect Email', shotCaption: 'Integrations → Email & Inbox — connect and manage channels.',
    summary: 'Link your inbox so outreach and replies live inside the CRM.',
    tags: ['email', 'gmail', 'zoho', 'smtp', 'imap', 'inbox'],
    cta: { label: 'Open Integrations', href: '/integrations' },
    reference: [
      ['Gmail', 'One-click OAuth connect for Google Workspace / Gmail.'],
      ['Zoho', 'OAuth connect for Zoho Mail.'],
      ['SMTP / IMAP', 'Any provider — send via SMTP, read replies via IMAP.'],
      ['Reconnect', 'Silently re-validates saved credentials — no password re-entry.'],
    ],
    steps: [
      { title: 'Choose a provider', text: 'Go to Integrations → Mail and connect via Gmail, Zoho, or SMTP/IMAP.' },
      { title: 'Validate the connection', text: 'After connecting, use the Reconnect button any time to silently re-validate saved credentials — no password re-entry.' },
      { title: 'Send & receive in-app', text: 'Send from lead/deal views; replies are threaded back automatically.' },
      { title: 'Remove safely', text: 'To remove an integration, type the confirmation word (e.g. "SMTP") so it can never be deleted by accident.' },
    ],
    best: 'Use a dedicated sending address with SPF/DKIM set up to keep deliverability high.',
  },
  {
    id: 'run-a-cadence', icon: Send, shot: 'campaigns', group: 'Outreach',
    title: 'Run a Campaign', shotCaption: 'Campaigns — outbound over Voice, Email or WhatsApp.',
    summary: 'Reach a whole audience over Voice, Email or WhatsApp — automatically.',
    tags: ['campaign', 'cadence', 'voice', 'email', 'whatsapp', 'audience', 'outreach', 'sequence'],
    plan: 'Growth', cta: { label: 'Open Campaigns', href: '/campaigns' },
    reference: [
      ['Channel', 'Voice, Email or WhatsApp — pick per campaign.'],
      ['Audience', 'The batch of leads a campaign targets.'],
      ['Calls/Day', 'The pace cap that protects deliverability & limits.'],
      ['Status', 'Active / Paused / Draft — play or pause anytime.'],
    ],
    steps: [
      { title: 'Build an audience', text: 'Build an Audience first — group the leads you want to target into a batch (from the Leads board or the Audience page).' },
      { title: 'Create the campaign', text: 'In Campaigns, click New, pick a channel (Voice, Email or WhatsApp) and choose the message template for that channel.' },
      { title: 'Set pace & attach audience', text: 'Set the pace (e.g. max per day) and priority, then attach your audience. Contactless leads are skipped until they have an email/phone.' },
      { title: 'Launch & track', text: 'Launch it — then Play/Pause any campaign and track calls/sends, replies and outcomes right on the campaign.' },
    ],
    best: 'Keep the first message short and personal. Long, salesy openers tank reply and answer rates.',
  },
  {
    id: 'linkedin-extension', icon: Chrome, shot: 'linkedin', group: 'Outreach',
    title: 'Use the LinkedIn Extension', shotCaption: 'LinkedIn Outbox — queue connects & messages, sent human-like.',
    summary: 'Send connection requests and messages at scale, safely.',
    tags: ['linkedin', 'extension', 'chrome', 'outbox', 'connect'],
    plan: 'Growth', cta: { label: 'Open LinkedIn Outbox', href: '/linkedin-outbox' },
    reference: [
      ['Total Tasks', 'Everything queued for LinkedIn.'],
      ['Pending / Approval', 'Waiting to send vs. waiting for your review.'],
      ['Send window', 'When the extension is allowed to send.'],
      ['Daily limits', 'Safe caps on connects/messages to protect your account.'],
    ],
    steps: [
      { title: 'Install & sign in', text: 'Install the Lockene extension from the Chrome Web Store and sign in with your CRM account.' },
      { title: 'Queue from the Outbox', text: 'Queue connects/messages from the LinkedIn Outbox — the extension sends them from your own browser, human-like.' },
      { title: 'Premium is handled', text: 'Premium accounts get LinkedIn’s "Add a note / Send without a note" screen — the extension handles both automatically.' },
      { title: 'Stay within limits', text: 'Stay within the daily connect/message limits shown on the Outbox to protect your LinkedIn account.' },
    ],
    best: 'Keep connection notes under 300 characters so they send on both Free and Premium accounts.',
  },
  {
    id: 'reveal-and-reports', icon: Eye, shot: 'reports', group: 'Data & insights',
    title: 'Reveal Contacts & Reports', shotCaption: 'Reports — measure Sales, Pipeline and Tickets.',
    summary: 'Unlock verified contact info and measure what’s working.',
    tags: ['reveal', 'enrich', 'reports', 'analytics', 'credits'],
    cta: { label: 'Open Reports', href: '/reports' },
    reference: [
      ['Reveal', 'Enriches a verified email/phone using reveal credits.'],
      ['Bulk reveal', 'Reveal a whole column or an ICP-matched set at once.'],
      ['Reports', 'Sales, Pipeline and Tickets analytics dashboards.'],
      ['Export CSV', 'Download any filtered view for a deck or hand-off.'],
    ],
    steps: [
      { title: 'Reveal a contact', text: 'On a lead, click Reveal to enrich verified email/phone — this uses reveal credits from your plan.' },
      { title: 'Bulk reveal', text: 'Bulk-reveal a whole column or an ICP-matched set from the Leads board when you need volume.' },
      { title: 'Read your reports', text: 'Track outcomes in Reports: engagement, outreach pipeline, and finance dashboards.' },
      { title: 'Export a view', text: 'Export any filtered view to CSV for a board deck or a teammate hand-off.' },
    ],
    best: 'Reveal only leads you intend to contact this week — it keeps credit spend tied to real pipeline.',
  },
];
